import { useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { EricActivityTable } from '@/components/admin/EricActivityTable'
import { UserActivityTable } from '@/components/admin/UserActivityTable'
import { formatDate, formatRelative } from '@/lib/dates'
import { formatMoney } from '@/lib/format'
import { DataSafetyTab } from './DataSafetyTab'

type Tenant = {
  id: string
  name: string
  city: string | null
  province: string | null
  country: string | null
  phone: string | null
  email: string | null
  labor_rate_cad: number | null
  tax_rate: number | null
  tax_label: string | null
  currency: string | null
  assistant_name: string | null
  clerk_org_id: string | null
  provisioned_at: string | null
  suspended_at: string | null
  created_at: string | null
  status: 'active' | 'pending_invite' | 'suspended'
}

type Member = {
  clerk_user_id: string | null
  email: string | null
  name: string | null
  role: string | null
  joined_at: string | null
}

type Supplier = { supplier: string; connected: boolean }

type Kpis = {
  estimates_count: number
  approved_count: number
  calls_count: number
  successful_calls_count: number
  ai_cost_cad_30d: number
  ai_tokens_total_30d: number
}

type Resp = {
  tenant: Tenant
  members: Member[]
  suppliers: Supplier[]
  kpis: Kpis
}

export function AdminTenantDetail() {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation('admin')
  const { t: tCommon } = useTranslation('common')
  const { getToken, isLoaded } = useAuth()
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<
    | 'overview'
    | 'members'
    | 'suppliers'
    | 'usage'
    | 'activity'
    | 'eric_activity'
    | 'user_activity'
    | 'combined'
    | 'data_safety'
  >('overview')

  const query = useQuery<Resp>({
    queryKey: ['admin', 'tenant', id],
    enabled: isLoaded && !!id,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch(`/api/admin/tenants/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (res.status === 404) throw new Error('not_found')
      if (!res.ok) {
        throw new Error(
          tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as Resp
    },
  })

  const suspendMut = useMutation({
    mutationFn: async (action: 'suspend' | 'reinstate') => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch(`/api/admin/tenants/${id}/${action}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        throw new Error(
          tCommon('error.request_failed', { status: res.status }),
        )
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenant', id] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] })
    },
  })

  const reinviteMut = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch(`/api/admin/tenants/${id}/reinvite-owner`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      })
      if (!res.ok) {
        throw new Error(
          tCommon('error.request_failed', { status: res.status }),
        )
      }
      return res.json()
    },
  })

  if (query.isError) {
    const message = query.error instanceof Error ? query.error.message : ''
    return (
      <div className="space-y-3">
        <Link
          to="/"
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          {t('tenants.back')}
        </Link>
        <Card className="border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {message === 'not_found'
            ? t('tenant_detail.not_found')
            : message || t('tenant_detail.error')}
        </Card>
      </div>
    )
  }

  if (!query.data) {
    return (
      <Card className="p-4 text-sm text-muted-foreground">
        {tCommon('loading')}
      </Card>
    )
  }

  const { tenant, members, suppliers, kpis } = query.data
  const owners = members.filter(
    (m) => m.role === 'org:owner' || m.role === 'org:admin',
  ).length
  const others = members.length - owners

  return (
    <div className="space-y-5">
      <Link
        to="/"
        className="text-xs text-muted-foreground hover:text-foreground"
      >
        {t('tenants.back')}
      </Link>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              {tenant.name}
            </h1>
            {tenant.status === 'active' ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">
                <span className="size-1.5 rounded-full bg-emerald-600" />
                {t('tenants.status.active')}
              </span>
            ) : tenant.status === 'pending_invite' ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
                <span className="size-1.5 rounded-full bg-amber-500" />
                {t('tenants.status.pending_invite')}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-800">
                <span className="size-1.5 rounded-full bg-rose-500" />
                {t('tenants.status.suspended')}
              </span>
            )}
          </div>
          <div className="mt-1 text-sm text-muted-foreground">
            {[tenant.city, tenant.province].filter(Boolean).join(', ') || '—'}
            {tenant.created_at
              ? ` · ${t('tenant_detail.created_at', {
                  date: formatDate(tenant.created_at),
                  lastSeen: t('tenant_detail.never_seen'),
                })}`
              : null}
          </div>
          <div className="mt-1.5 font-mono text-[11px] text-muted-foreground">
            {tenant.id} · {tenant.clerk_org_id ?? '—'}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => reinviteMut.mutate()}
            disabled={reinviteMut.isPending || !tenant.clerk_org_id}
          >
            {t('tenant_detail.re_invite_owner')}
          </Button>
          {tenant.status === 'suspended' ? (
            <Button
              size="sm"
              onClick={() => suspendMut.mutate('reinstate')}
              disabled={suspendMut.isPending}
            >
              {t('tenant_detail.reinstate')}
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (window.confirm(t('tenant_detail.suspend_confirm'))) {
                  suspendMut.mutate('suspend')
                }
              }}
              disabled={suspendMut.isPending}
            >
              {t('tenant_detail.suspend')}
            </Button>
          )}
        </div>
      </div>

      {reinviteMut.isSuccess ? (
        <Card className="border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-800">
          {t('tenant_detail.re_invite_sent')}
        </Card>
      ) : null}

      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard
          label={t('tenant_detail.kpis.members')}
          value={String(members.length)}
          meta={t('tenant_detail.kpis.members_breakdown', { owners, others })}
        />
        <KpiCard
          label={t('tenant_detail.kpis.estimates_30d')}
          value={String(kpis.estimates_count)}
          meta={t('tenant_detail.kpis.estimates_meta', {
            count: kpis.approved_count,
            percent:
              kpis.estimates_count > 0
                ? Math.round(
                    (kpis.approved_count / kpis.estimates_count) * 100,
                  )
                : 0,
          })}
        />
        <KpiCard
          label={t('tenant_detail.kpis.calls_30d')}
          value={String(kpis.calls_count)}
          meta={t('tenant_detail.kpis.calls_meta', {
            count: kpis.successful_calls_count,
            percent:
              kpis.calls_count > 0
                ? Math.round(
                    (kpis.successful_calls_count / kpis.calls_count) * 100,
                  )
                : 0,
          })}
        />
        <KpiCard
          label={t('tenant_detail.kpis.ai_cost_30d')}
          value={formatMoney(kpis.ai_cost_cad_30d)}
          meta={t('tenant_detail.kpis.ai_cost_meta', {
            tokens: kpis.ai_tokens_total_30d.toLocaleString(),
          })}
        />
      </div>

      {/* Tabs — horizontal-scroll at <md so the row fits on a phone
          without wrapping. The tabs aren't admin's primary nav; mobile
          users mostly come here to glance at one tenant. */}
      <div className="border-b border-border">
        <div className="-mx-1 flex items-center gap-1 overflow-x-auto px-1">
          {(
            [
              ['overview', t('tenant_detail.tabs.overview')],
              ['members', t('tenant_detail.tabs.members')],
              ['suppliers', t('tenant_detail.tabs.suppliers')],
              ['usage', t('tenant_detail.tabs.usage')],
              ['activity', t('tenant_detail.tabs.activity')],
              ['eric_activity', t('tenant_detail.tabs.eric_activity')],
              ['user_activity', t('tenant_detail.tabs.user_activity')],
              ['combined', t('tenant_detail.tabs.combined')],
              ['data_safety', 'Data safety'],
            ] as const
          ).map(([k, label]) => (
            <button
              type="button"
              key={k}
              onClick={() => setTab(k)}
              className={
                tab === k
                  ? 'whitespace-nowrap border-b-2 border-brand px-3 pb-2 pt-1 text-sm font-semibold text-foreground'
                  : 'whitespace-nowrap px-3 pb-2 pt-1 text-sm text-muted-foreground hover:text-foreground'
              }
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'overview' ? (
        <OverviewTab tenant={tenant} suppliers={suppliers} members={members} />
      ) : null}
      {tab === 'members' ? <MembersTab members={members} /> : null}
      {tab === 'suppliers' ? (
        <SuppliersOnlyTab suppliers={suppliers} />
      ) : null}
      {tab === 'usage' ? (
        <Card className="p-5 text-sm text-muted-foreground">
          <Link to={`/usage?shop=${tenant.id}`} className="text-brand">
            {t('nav.usage')} →
          </Link>
        </Card>
      ) : null}
      {tab === 'activity' ? (
        <Card className="p-5 text-sm text-muted-foreground">
          {t('tenant_detail.activity.empty')}
        </Card>
      ) : null}
      {tab === 'eric_activity' ? (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            {t('tenant_detail.eric_activity_tab.subtitle')}
          </p>
          <EricActivityTable
            shopId={tenant.id}
            defaultRange="30d"
            hideShopColumn={true}
            showFilters={true}
            queryKeyPrefix={`admin.eric-activity.tenant.${tenant.id}`}
          />
        </div>
      ) : null}
      {tab === 'user_activity' ? (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            {t('tenant_detail.user_activity_tab.subtitle')}
          </p>
          <UserActivityTable
            shopId={tenant.id}
            hideShopColumn={true}
            defaultRange="7d"
            queryKeyPrefix={`admin.user-activity.tenant.${tenant.id}`}
          />
        </div>
      ) : null}
      {tab === 'combined' ? (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            {t('tenant_detail.combined_tab.subtitle')}
          </p>
          <UserActivityTable
            shopId={tenant.id}
            combined={true}
            hideShopColumn={true}
            defaultRange="7d"
            queryKeyPrefix={`admin.combined-activity.tenant.${tenant.id}`}
          />
        </div>
      ) : null}
      {tab === 'data_safety' ? (
        <DataSafetyTab tenantId={tenant.id} tenantName={tenant.name} />
      ) : null}
    </div>
  )
}

function KpiCard({
  label,
  value,
  meta,
}: {
  label: string
  value: string
  meta?: string
}) {
  return (
    <Card className="p-4">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 text-2xl font-bold text-foreground">{value}</div>
      {meta ? (
        <div className="mt-0.5 text-xs text-muted-foreground">{meta}</div>
      ) : null}
    </Card>
  )
}

function OverviewTab({
  tenant,
  suppliers,
  members,
}: {
  tenant: Tenant
  suppliers: Supplier[]
  members: Member[]
}) {
  const { t } = useTranslation('admin')
  return (
    <div className="grid gap-4 md:grid-cols-3">
      <Card className="p-5 md:col-span-2">
        <div className="mb-3 text-xs font-bold uppercase tracking-wide text-muted-foreground">
          {t('tenant_detail.members.header')}
        </div>
        <MembersTable members={members} />
      </Card>
      <div className="space-y-4">
        <SuppliersCard suppliers={suppliers} />
        <ProvisioningCard tenant={tenant} />
      </div>
    </div>
  )
}

function MembersTab({ members }: { members: Member[] }) {
  return (
    <Card className="p-5">
      <MembersTable members={members} />
    </Card>
  )
}

function MembersTable({ members }: { members: Member[] }) {
  const { t } = useTranslation('admin')
  if (members.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">
        {t('tenant_detail.members.empty')}
      </div>
    )
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
            <th className="py-2 pr-4 font-semibold">
              {t('tenant_detail.members.table.name')}
            </th>
            <th className="py-2 pr-4 font-semibold">
              {t('tenant_detail.members.table.email')}
            </th>
            <th className="py-2 pr-4 font-semibold">
              {t('tenant_detail.members.table.role')}
            </th>
            <th className="py-2 font-semibold">
              {t('tenant_detail.members.table.joined')}
            </th>
          </tr>
        </thead>
        <tbody>
          {members.map((m, idx) => (
            <tr
              key={m.clerk_user_id ?? m.email ?? `idx-${idx}`}
              className="border-b border-border/40 last:border-0"
            >
              <td className="py-2 pr-4 font-medium text-foreground">
                {m.name ?? '—'}
              </td>
              <td className="py-2 pr-4 text-foreground">{m.email ?? '—'}</td>
              <td className="py-2 pr-4">
                <span className="inline-flex items-center rounded-full bg-brand-light px-2 py-0.5 text-[11px] font-semibold text-brand-dark">
                  {m.role ?? '—'}
                </span>
              </td>
              <td className="py-2 text-xs text-muted-foreground">
                {m.joined_at ? formatRelative(m.joined_at) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function SuppliersOnlyTab({ suppliers }: { suppliers: Supplier[] }) {
  return (
    <Card className="p-5">
      <SuppliersList suppliers={suppliers} />
    </Card>
  )
}

function SuppliersCard({ suppliers }: { suppliers: Supplier[] }) {
  const { t } = useTranslation('admin')
  return (
    <Card className="p-5">
      <div className="mb-3 text-xs font-bold uppercase tracking-wide text-muted-foreground">
        {t('tenant_detail.supplier_card.header')}
      </div>
      <SuppliersList suppliers={suppliers} />
    </Card>
  )
}

function SuppliersList({ suppliers }: { suppliers: Supplier[] }) {
  const { t } = useTranslation('admin')
  return (
    <div className="space-y-3">
      {suppliers.map((s) => (
        <div key={s.supplier} className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-foreground capitalize">
              {s.supplier === 'worldpac'
                ? t('tenant_detail.supplier_card.worldpac_label')
                : s.supplier}
            </div>
            <div className="text-[11px] text-muted-foreground">
              {s.supplier === 'worldpac'
                ? t('tenant_detail.supplier_card.worldpac_caption')
                : ''}
            </div>
          </div>
          {s.connected ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">
              <span className="size-1.5 rounded-full bg-emerald-600" />
              {t('tenant_detail.supplier_card.connected')}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
              <span className="size-1.5 rounded-full bg-muted-foreground/60" />
              {t('tenant_detail.supplier_card.not_connected')}
            </span>
          )}
        </div>
      ))}
    </div>
  )
}

function ProvisioningCard({ tenant }: { tenant: Tenant }) {
  const { t } = useTranslation('admin')
  return (
    <Card className="p-5">
      <div className="mb-3 text-xs font-bold uppercase tracking-wide text-muted-foreground">
        {t('tenant_detail.provisioning_card.header')}
      </div>
      <dl className="space-y-2 text-xs">
        <div className="flex justify-between gap-3">
          <dt className="text-muted-foreground">
            {t('tenant_detail.provisioning_card.clerk_org')}
          </dt>
          <dd className="truncate font-mono text-right">
            {tenant.clerk_org_id ?? '—'}
          </dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-muted-foreground">
            {t('tenant_detail.provisioning_card.shop_row')}
          </dt>
          <dd>
            {tenant.provisioned_at ? (
              <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">
                {t('tenant_detail.provisioning_card.provisioned_yes')}
              </span>
            ) : (
              <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
                {t('tenant_detail.provisioning_card.provisioned_no')}
              </span>
            )}
          </dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-muted-foreground">
            {t('tenant_detail.provisioning_card.labor_rate')}
          </dt>
          <dd>
            {tenant.labor_rate_cad != null
              ? `${formatMoney(tenant.labor_rate_cad)}/h`
              : '—'}
          </dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-muted-foreground">
            {t('tenant_detail.provisioning_card.tax')}
          </dt>
          <dd>
            {tenant.tax_rate != null
              ? `${(tenant.tax_rate * 100).toFixed(0)}% ${
                  tenant.tax_label ?? ''
                }`
              : '—'}
          </dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-muted-foreground">
            {t('tenant_detail.provisioning_card.assistant')}
          </dt>
          <dd>
            {tenant.assistant_name ??
              t('tenant_detail.provisioning_card.no_assistant')}
          </dd>
        </div>
      </dl>
    </Card>
  )
}
